/**
 * Partner co-branding configuration
 * Maps affiliate source codes (s= parameter) to partner branding details
 */

export interface PartnerConfig {
  source: string; // The value from s= parameter (also the URL path slug, e.g. /goodlab/...)
  partnerName: string;
  referenceName: string; // Public-facing name (e.g. "Apex Focus Group" for 3M Sales)
  partnerLogoUrl: string;
  // Canonical medium (m=) to reconstruct when the query param is stripped in transit.
  // Falls back to "affiliates" when omitted. See resolveAttribution() in utils/attribution.ts.
  defaultMedium?: string;
}

/**
 * Configuration for partner co-branding based on affiliate source
 * Add new partner configurations here as needed
 */
export const partnerConfigs: PartnerConfig[] = [
  {
    source: "rxtra",
    partnerName: "RxTra",
    referenceName: "RxTra Clinical",
    partnerLogoUrl: "/assets/partners/rxtra-logo.png",
  },
  {
    source: "goodlab",
    partnerName: "GoodLab",
    referenceName: "GoodLab",
    partnerLogoUrl: "/assets/partners/goodlab-logo.png",
  },
  {
    source: "teverydayhealth",
    partnerName: "Everyday Health",
    referenceName: "Everyday Health",
    partnerLogoUrl: "/assets/partners/everyday-health-logo.png",
  },
  {
    source: "clinispan",
    partnerName: "CliniSpan",
    referenceName: "CliniSpan Health",
    partnerLogoUrl: "/assets/partners/clinispan-logo.png",
  },
  {
    source: "science37",
    partnerName: "Science 37",
    referenceName: "Science 37",
    partnerLogoUrl: "/assets/partners/science-37-logo.png",
  },
  {
    source: "clinicalhero",
    partnerName: "Clinical Hero",
    referenceName: "Clinical Hero",
    partnerLogoUrl: "/assets/partners/clinical-hero-logo.png",
  },
  {
    source: "severalbrands",
    partnerName: "Several Brands",
    referenceName: "Top Clinical Trials",
    partnerLogoUrl: "/assets/partners/severalbrands-logo.png",
  },
  {
    source: "brightfire",
    partnerName: "Brightfire",
    referenceName: "CareHealth",
    partnerLogoUrl: "/assets/partners/brightfire-logo.png",
  },
  {
    source: "diablomedia",
    partnerName: "Diablo Media",
    referenceName: "Everyday Resources",
    partnerLogoUrl: "/assets/partners/diablo-media-logo.png",
  },
  {
    source: "gumballdigital",
    partnerName: "Gumball Digital",
    referenceName: "Active Trials",
    partnerLogoUrl: "/assets/partners/gumball-digital-logo.png",
  },
  {
    source: "sparktonic",
    partnerName: "SparkTonic",
    referenceName: "New Path Trials",
    partnerLogoUrl: "/assets/partners/sparktonic-logo.png",
  },
  {
    source: "fluxmarketing",
    partnerName: "Flux Marketing",
    referenceName: "YourBetterLiving",
    partnerLogoUrl: "/assets/partners/flux-marketing-logo.png",
  },
  {
    source: "3msales",
    partnerName: "3M Sales",
    referenceName: "Apex Focus Group",
    partnerLogoUrl: "/assets/partners/3m-sales-logo.png",
  },
  {
    source: "clinrol",
    partnerName: "Clinrol",
    referenceName: "Clinrol",
    partnerLogoUrl: "/assets/partners/clinrol-logo.png",
  },
  {
    source: "penguinwolf",
    partnerName: "PenguinWolf",
    referenceName: "CareQuest",
    partnerLogoUrl: "/assets/partners/penguinwolf-logo.png",
  },
  {
    source: "imagebloom",
    partnerName: "ImageBloom",
    referenceName: "ImageBloom",
    partnerLogoUrl: "/assets/partners/imagebloom-logo.png",
  },
  {
    source: "responsumhealth",
    partnerName: "Responsum Health",
    referenceName: "Responsum Health",
    partnerLogoUrl: "/assets/partners/responsum-health-logo.png",
  },
  {
    source: "clinifi",
    partnerName: "CliniFi",
    referenceName: "CliniFi",
    partnerLogoUrl: "/assets/partners/clinifi-logo.png",
  },
  {
    source: "cssi",
    partnerName: "CSSi",
    referenceName: "CSSi",
    partnerLogoUrl: "/assets/partners/cssi-logo.png",
  },
  {
    source: "buildclinical",
    partnerName: "BuildClinical",
    referenceName: "BuildClinical",
    partnerLogoUrl: "/assets/partners/buildclinical-logo.png",
  },
  {
    source: "hmtest",
    partnerName: "hmtest",
    referenceName: "Test Company Name",
    partnerLogoUrl: "/assets/partners/hmtest-logo.png",
  },
  {
    source: "jaspari",
    partnerName: "Jaspari",
    referenceName: "Jaspari",
    partnerLogoUrl: "/assets/partners/jaspari-logo.png",
  },
];

/**
 * Get partner configuration by source code
 * @param source - The affiliate source code from s= parameter
 * @returns Partner configuration if found, undefined otherwise
 */
export function getPartnerConfigBySource(
  source: string | null | undefined,
): PartnerConfig | undefined {
  if (!source) return undefined;
  
  return partnerConfigs.find(
    (config) => config.source.toLowerCase() === source.toLowerCase(),
  );
}